import React, { Component } from 'react';
import _ from 'lodash';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Button, Header, Segment, Form, Divider, Message, List, Icon, Popup } from 'semantic-grab-ui/src';
import type { RequestStates } from 'types/redux';
import type { TridentCampaignDetails, CampaignEditRecord } from 'types/trident-campaigns';
import type { Country } from 'types/country';
import Toastr from 'utils/toastr-util';
import { RuleType } from 'types/ts/rule-types';
import { hasTridentPermission } from 'omega-rtc/utils/user-utils';
import { parseUCDSyncErrMsg } from 'omega-rtc/utils/ucd-utils';
import { isRequestsPending, hasJustSuccess } from 'utils/request-state-utils';
import type { StoreState } from 'reducers';
import type { UserState } from 'reducers/user';
import { DetailsHeader } from 'components/custom';
import ProgressiveContainer from 'components/container/ProgressiveContainer';
import { getTarget } from 'actions/trident-campaigns';
import { getTemplateList, getListRuleTypes, doCampaignApprovalFlowAction, campaignApprovalFlowActions, getApprovalFlowConfig } from 'actions/newTridentExtra';
import { CampaignStatus, hedwigTemplateStatus, CampaignChangeType } from 'data/trident-campaigns';
import CampaignOverviewReview from './CampaignOverviewReview';
import ElementBreakdownReview from './ElementBreakdownReview';
import WarningChecklist from './WarningChecklist';
import ApproverSelectionInput from './ApproverSelectionInput';
import { getCampaignElements, getHedwigTemplateUrl } from './campaign-analysis';
import { checkCampaign } from './campaign-check';
import TridentCampaignSummary from 'views/trident-campaigns/common/TridentCampaignSummary';
import 'views/trident-campaigns/TridentCampaigns.scss';
import './TridentCampaignReviewPage.scss';
import scribe from '@grab-x/scribe-web-sdk';
import ScribeEventName from '../../../constants/scribe';
import { processCampaignData, sendOpenEvent } from '../../../utils/scribe';


interface Props {
  getTarget: (webPath: string, campaignID: string, params: object) => void;
  getTemplateList: (templateIDs: string[]) => void;
  doCampaignApprovalFlowAction: (countryID: number, campaignID: string, actionType: string, data: object) => void;
  getApprovalFlowConfig: () => void;
  getCampaignRequest: RequestStates;
  getTemplateListRequest: RequestStates;
  approvalFlowRequest: RequestStates;
  approvalFlowConfig?: object;
  templateMap: object;
  campaign: TridentCampaignDetails;
  campaignID: string;
  countryID: number;
  webPath: string;
  getCountriesRequest: RequestStates;
  countries: Country[];
  getListRuleTypes: (params: object) => void;
  listRuleTypes: RuleType[];
  user: UserState;
  userPermissions: object;
  ucdErrMessage?: string;
}

interface State {
  elements: object;
  warnings: object;
  comment: string;
  approvers: string[];
}

// TemplateInfoMessage is a simple component to show a list of templates
const TemplateInfoMessage = (props: { links: string[], header?: string, color?: string, negative?: boolean }) => {
  const { links, header, color, negative } = props;
  return (
    <React.Fragment>
      {
        !_.isEmpty(links) && <Message color={color} negative={negative}>
          <Message.Header>{header}</Message.Header>
          <List bulleted>
            {
              _.map(links, (e, index) => {
                return <List.Item key={index}><a href={e} target="_blank">{e}</a></List.Item>;
              })
            }
          </List>
        </Message>
      }
    </React.Fragment>
  );
};

export class TridentCampaignReviewPage extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      elements: {},
      warnings: {},
      comment: '',
      approvers: [],
    };
  }

  componentDidMount() {
    this.props.getTarget('', this.props.campaignID, {
      countryID: this.props.countryID,
    });
    this.props.getApprovalFlowConfig();
    sendOpenEvent(_.get(this.props.user, 'email'), { campaignId: this.props.campaignID });
  }

  componentWillReceiveProps(nextProps: Props) {
    if (hasJustSuccess(this.props.getCampaignRequest, nextProps.getCampaignRequest) && !_.isEmpty(nextProps.campaign)) {
      this.props.getListRuleTypes({ owner: nextProps.campaign.owner, targetUserType: nextProps.campaign.targetUserType });

      const elements = getCampaignElements(_.get(nextProps.campaign, 'nodes'));
      this.setState({
        elements,
      });

      if (!_.isEmpty(elements.templates)) {
        this.props.getTemplateList(_.map(elements.templates, t => { return t.id; }));
      } else {
        // if no template is involved, can just go ahead and do checking
        this.setState({
          warnings: checkCampaign(nextProps.campaign, {}),
        });
      }
    }

    if (hasJustSuccess(this.props.getTemplateListRequest, nextProps.getTemplateListRequest)) {
      const warnings = checkCampaign(this.props.campaign, {
        templateInfo: nextProps.templateMap,
      });
      this.setState({
        warnings,
      });
    }

    if (hasJustSuccess(this.props.approvalFlowRequest, nextProps.approvalFlowRequest)) {
      if (nextProps.ucdErrMessage) {
        const reason = parseUCDSyncErrMsg(nextProps.ucdErrMessage);
        Toastr.error(`Trident Campaign status updated successfully, but failed to update in Holos : ${reason}`);
      }
      // approval flow request success, reload the campaign
      this.props.getTarget('', this.props.campaignID, {
        countryID: this.props.countryID,
      });
    }
  }

  getRejectionMessage = (edit: CampaignEditRecord) => {
    return (
      <p>
        <b>{edit.editor}</b> rejected this campaign {
          !_.isEmpty(edit.comment) && <span> with a comment: <b>{edit.comment}</b></span>
        }
      </p>
    );
  };

  getApprovalRequestMessage = (edit: CampaignEditRecord) => {
    const { targetUserType, approvers, owner } = _.get(this.props, 'campaign', {});
    const canApprove = hasTridentPermission(this.props.userPermissions, targetUserType, owner, 'active');

    return (
      <React.Fragment>
        <p>
          <b>{edit.editor}</b> sent this campaign for approval {
            !_.isEmpty(edit.comment) && <span> with a comment: <b>{edit.comment}</b></span>
          }
        </p>
        {
          _.includes(approvers, this.props.user.email) && !canApprove && <p>
            You are selected as an approver for this campaign, but you do not have the permission to do so :/
          </p>
        }
      </React.Fragment>
    );
  };

  getReviewMessage = () => {
    const lastEdit = this.getLastEditRecord();
    switch (_.get(this.props.campaign, 'status')) {
      case CampaignStatus.draft:
        if (lastEdit.changeType === CampaignChangeType.reject) {
          return this.getRejectionMessage(lastEdit);
        }
        return 'This campaign is in DRAFT, and can be sent for approval';
      case CampaignStatus.inactive:
        return 'This campaign is INACTIVE, you need to edit and save it before you can send it for approval';
      case CampaignStatus.active:
        return 'This campaign is ACTIVE, deactivate it first if you want to make changes';
      case CampaignStatus.pending:
        if (lastEdit.changeType === CampaignChangeType.request) {
          return this.getApprovalRequestMessage(lastEdit);
        }
        return 'This campaign is pending approval';
      default:
        return '';
    }
  };

  getLastEditRecord() {
    const records = _.get(this.props.campaign, 'editRecords', []);
    if (_.isEmpty(records)) {
      return {};
    }
    return records[records.length - 1];
  }

  // return list of users that have created or updated the campaign before
  getPastEditors() {
    const records = _.get(this.props.campaign, 'editRecords', []);
    const editors = new Set();
    _.map(records, r => {
      if (r.changeType === CampaignChangeType.create || r.changeType === CampaignChangeType.update) {
        editors.add(r.editor);
      }
    });
    return [...editors];
  }

  getTemplateIDsWithStatus = (status: string) => {
    const templateIDs = [];
    _.map(this.props.templateMap, (info, templateID) => {
      if (_.get(info, 'templateStatus') === status) {
        templateIDs.push(templateID);
      }
    });
    return templateIDs;
  };

  getTemplateUrls = (ids: Array<string>) => {
    return _.map(ids, tid => {
      const ttype = _.get(this.props.templateMap, `${tid}.templateType`);
      return getHedwigTemplateUrl(ttype, tid);
    });
  };

  handleInputChange = (event: any, data: any) => {
    const name = _.get(data, 'name');
    const value = _.get(data, 'value');
    this.setState({
      [name]: value,
    });
  };

  handleApproverChange = (_: any, data: any) => {
    this.setState({
      approvers: data.value,
    });
  };

  isTemplateStatusCheckFailed = () => {
    const { campaign, templateMap } = this.props;
    const elements = getCampaignElements(_.get(campaign, 'nodes'));
    return !_.isEmpty(elements.templates) && _.isEmpty(templateMap);
  }

  handleSendForApproval = () => {
    // find templates that needs to be sent for approval as well
    const templateIDs = this.getTemplateIDsWithStatus(hedwigTemplateStatus.draft);
    if (_.isEmpty(this.state.approvers)) {
      alert('Please specify approver for this campaign');
      return;
    }

    // check whether template status are available
    if (this.isTemplateStatusCheckFailed()) {
      alert('Trident is NOT able to check hedwig template status. Please make sure you have hedwig access and try refreshing the page.');
      return;
    }

    const { countryID, campaignID } = _.get(this.props, 'campaign', {});
    this.props.doCampaignApprovalFlowAction(countryID, campaignID, campaignApprovalFlowActions.request, {
      adminEmail: this.props.user.email,
      templateIDs,
      comment: this.state.comment,
      notifyees: this.state.approvers,
      allowedApprovers: this.state.approvers,
      approvers: this.state.approvers, // This is only used for outside Grab ecosystem scenario where it calls trident without omega
    });

    scribe.sendEvent(
      ScribeEventName.CAMPAIGN_REQUESTED,
      processCampaignData(
        { ...this.props.campaign, user: this.props.user },
        { approvers: this.state.approvers, comment: this.state.comment },
      ),
    );

    // in case, the requester is the approver
    setTimeout(() => {
      scribe.sendEvent(
        ScribeEventName.FORM_OPEN,
        processCampaignData(
          { ...this.props.campaign, user: this.props.user },
          { approvers: this.state.approvers, comment: this.state.comment },
        ),
      );
    }, 10);
  };

  handlerWithdraw = () => {
    const { countryID, campaignID } = _.get(this.props, 'campaign', {});
    this.props.doCampaignApprovalFlowAction(countryID, campaignID, campaignApprovalFlowActions.withdraw, {
      adminEmail: this.props.user.email,
      notifyees: _.get(this.props, 'campaign.approvers', []),
    });

    scribe.sendEvent(
      ScribeEventName.CAMPAIGN_WITHDRAWN,
      processCampaignData(
        { ...this.props.campaign, user: this.props.user },
        { comment: this.state.comment },
      ),
    );
  };

  handleApproveCampaign = () => {
    // check whether template status are available
    if (this.isTemplateStatusCheckFailed()) {
      alert('Trident is NOT able to check hedwig template status. Please make sure you have hedwig access and try refreshing the page.');
      return;
    }

    if (confirm('Campaign will be activated once you approve, are you sure to do so?')) {
      // find templates that needs to be approved
      const templateIDs = this.getTemplateIDsWithStatus(hedwigTemplateStatus.pendingApproval);

      this.approveOrRejectCampaign(campaignApprovalFlowActions.approve, templateIDs);
    }
  };

  handleRejectCampaign = () => {
    this.approveOrRejectCampaign(campaignApprovalFlowActions.reject, []);
  };

  approveOrRejectCampaign = (actionType: string, templateIDs: Array<string>) => {
    const { countryID } = _.get(this.props, 'campaign', {});
    const lastEdit = this.getLastEditRecord();
    const notifyees = [];
    if (!_.isEmpty(lastEdit)) {
      notifyees.push(_.get(lastEdit, 'editor', ''));
    }

    const { campaignID } = _.get(this.props, 'campaign', {});

    this.props.doCampaignApprovalFlowAction(countryID, campaignID, actionType, {
      adminEmail: this.props.user.email,
      notifyees,
      templateIDs,
      comment: this.state.comment,
      status: actionType === campaignApprovalFlowActions.approve ? 1 : undefined, // This is only used for outside Grab ecosystem scenario where it calls trident without omega
    });

    scribe.sendEvent(
      actionType === 'reject'
        ? ScribeEventName.CAMPAIGN_REJECTED
        : ScribeEventName.CAMPAIGN_APPROVED,
      processCampaignData(
        { ...this.props.campaign, user: this.props.user },
        { comment: this.state.comment },
      ),
    );
  };

  render() {
    const { targetUserType, status, approvers, owner } = _.get(this.props, 'campaign', {});
    const lastEdit = this.getLastEditRecord();
    const allowSelfApprove = _.get(this.props.approvalFlowConfig, 'allowSelfApprove', true);

    // template info to display for approval flow action
    let templateIDsToUpdate = [];
    let invalidTemplateIDs = []; // templates that are not in the expected status
    let templateInfoHeader;
    let templateErrorHeader;
    if (status === CampaignStatus.draft) {
      templateIDsToUpdate = this.getTemplateIDsWithStatus(hedwigTemplateStatus.draft);
      invalidTemplateIDs = this.getTemplateIDsWithStatus(hedwigTemplateStatus.inactive);
      templateInfoHeader = 'Templates to be sent for approval:';
      templateErrorHeader = 'Inactive templates are not allowed, please remove them:';
    } else if (status === CampaignStatus.pending) {
      templateIDsToUpdate = this.getTemplateIDsWithStatus(hedwigTemplateStatus.pendingApproval);
      invalidTemplateIDs = _.concat(this.getTemplateIDsWithStatus(hedwigTemplateStatus.inactive), this.getTemplateIDsWithStatus(hedwigTemplateStatus.draft));
      templateInfoHeader = 'Templates to be activated:';
      templateErrorHeader = 'Inactive or draft templates are not allowed, please fix them:';
    }
    const templatesToUpdate = this.getTemplateUrls(templateIDsToUpdate);
    const invalidTemplates = this.getTemplateUrls(invalidTemplateIDs);

    // access control decisions for approval flow
    const allowApprove = hasTridentPermission(this.props.userPermissions, targetUserType, owner, 'active')
      && _.includes(approvers, this.props.user.email)
      && status === CampaignStatus.pending
      && _.isEmpty(invalidTemplates);

    const allowWithdraw = hasTridentPermission(this.props.userPermissions, targetUserType, owner, 'update')
      && status === CampaignStatus.pending
      && this.props.user.email === _.get(lastEdit, 'editor');

    const allowSendForApproval = hasTridentPermission(this.props.userPermissions, targetUserType, owner, 'update')
      && status === CampaignStatus.draft
      && _.isEmpty(invalidTemplates);

    const allowComment = allowApprove || allowSendForApproval;
    const commentPlaceHolder = allowApprove ? 'Type in your reason if you choose to reject ...' : 'Type in your comment here ...';

    const pastEditors = this.getPastEditors();
    const excludedApprovers = allowSelfApprove ? [] : pastEditors;

    const isLoading = isRequestsPending(
      this.props.getCampaignRequest,
      this.props.getCountriesRequest,
      this.props.getTemplateListRequest,
    );

    const isApprovalFlowRequestOngoing = isRequestsPending(this.props.approvalFlowRequest);

    const editPageLink = (
      <Button primary as={Link} to={`${this.props.webPath}/${this.props.countryID}/${this.props.campaignID}/edit`}>
        Go to Edit
      </Button>
    );

    // campaign review UI
    const panels = [
      {
        name: 'Overview',
        view: <CampaignOverviewReview countries={this.props.countries} campaign={this.props.campaign} />,
      },
      {
        name: 'Element Breakdown',
        view: <ElementBreakdownReview elements={this.state.elements} campaign={this.props.campaign} />,
      },
      {
        name: 'Checklist Warnings',
        view: !_.isEmpty(this.state.warnings) ? <WarningChecklist
          warnings={this.state.warnings}
          nodes={_.get(this.props.campaign, 'nodes', {})}
          nodesOccMap={_.get(this.props.campaign, 'nodesOccMap', {})}
        /> : null,
        headerClass: 'warning-header',
      },
      {
        name: 'Campaign Setup',
        view: <TridentCampaignSummary campaign={this.props.campaign} listRuleTypes={this.props.listRuleTypes} warnings={_.get(this.state.warnings, 'nodes', {})} hideUsage />,
      },
    ];

    const isApproverEndpointAvailable = _.get(window.Grab.config, 'IS_OMEGA_BACKEND_AVAILABLE', true);

    return (
      <DocumentTitle title="Review Campaign">
        <div>
          <DetailsHeader
            detailsTitle={_.get(this.props.campaign, 'name', '')}
            primaryButton={editPageLink}
          />
          <ProgressiveContainer isLoading={isLoading}>
            {
              _.map(panels, (panel) => {
                return !_.isEmpty(panel.view) &&
                  <div key={panel.name}>
                    <p className="clear-top" />
                    <Header as="h3" attached="top" className={panel.headerClass}>
                      {panel.name}
                    </Header>
                    <Segment attached>
                      {panel.view}
                    </Segment>
                  </div>;
              })
            }

            <Divider horizontal>
              <Header as="h4">
                Actions
              </Header>
            </Divider>

            <Message info>
              {this.getReviewMessage()}
            </Message>

            <TemplateInfoMessage links={templatesToUpdate} header={templateInfoHeader} />
            <TemplateInfoMessage links={invalidTemplates} header={templateErrorHeader} negative />

            <Form>
              {
                allowComment && <Form.TextArea label="Comment" placeholder={commentPlaceHolder} name="comment" onChange={this.handleInputChange} />
              }
              {
                allowSendForApproval && <React.Fragment>
                  <Form.Field>
                    <label>
                      Select Approver
                      {
                        _.includes(excludedApprovers, this.props.user.email) &&
                          <span>
                            &nbsp;
                            <Popup
                              content="You are not allowed to send to yourself for approval because you have created or updated the campaign before"
                              trigger={<Icon name="info circle" />}
                            />
                          </span>
                      }
                    </label>
                    {isApproverEndpointAvailable ? <ApproverSelectionInput
                      onChange={this.handleApproverChange}
                      excludeList={excludedApprovers}
                    /> : <Form.Input onChange={(event: any, data: any) => {
                      this.setState({
                        approvers: data.value.split(','),
                      });
                    }}
                    />
                    }
                  </Form.Field>
                  <Button color="blue" loading={isApprovalFlowRequestOngoing} onClick={this.handleSendForApproval}>Send For Approval</Button>
                </React.Fragment>
              }
              {
                allowWithdraw && <Button loading={isApprovalFlowRequestOngoing} onClick={this.handlerWithdraw}>Withdraw</Button>
              }
              {
                allowApprove && <React.Fragment>
                  <Button color="red" loading={isApprovalFlowRequestOngoing} onClick={this.handleRejectCampaign}>Reject</Button>
                  <Button primary loading={isApprovalFlowRequestOngoing} onClick={this.handleApproveCampaign}>Approve & Activate</Button>
                </React.Fragment>
              }
            </Form>
          </ProgressiveContainer>
        </div>
      </DocumentTitle>
    );
  }
}


function mapStateToProps(state: StoreState, ownProps: object) {
  const {
    stateKey,
    params = {},
    webPath,
  } = ownProps as { stateKey: string, params?: { entityID?: string, countryID?: number }, webPath: string };
  const entityState = _.get(state, stateKey, {});
  return {
    webPath,
    userPermissions: state.app.user.userPermissions,
    user: state.app.user,
    campaignID: params.entityID,
    countryID: params.countryID,
    campaign: entityState.active,
    getCampaignRequest: _.get(state.requests, `getEntity${_.upperFirst(stateKey)}Request`, {}),
    templateMap: _.get(state, 'newTridents.templateMap', {}),
    getTemplateListRequest: _.get(state.requests, 'getTemplateListTridentCampaignsRequest', {}),
    approvalFlowRequest: _.get(state.requests, 'campaignApprovalFlowRequest', {}),
    countries: state.countries.entities,
    getCountriesRequest: state.requests.getCountriesRequest || {},
    listRuleTypes: _.get(state, 'newTridents.listRuleTypes', {}),
    approvalFlowConfig: _.get(state, 'newTridents.approvalFlowConfig', {}),
    ucdErrMessage: _.get(state, 'newTridents.ucdErrMessage', ''),
  };
}

export default connect(mapStateToProps, {
  getTarget,
  getTemplateList,
  getListRuleTypes,
  doCampaignApprovalFlowAction,
  getApprovalFlowConfig,
})(TridentCampaignReviewPage);
